// Shared behavior for corner dropdown panels (FocusTrap, keyboard, backdrop close)

"use strict";

import { onBeforeUnmount, onMounted, watch, type Ref } from "vue";
import { autoFocusElement } from "@/composables/useAutoFocus";
import { FocusTrap } from "@/utils/focus-trap";

export type UseDropdownPanelOptions = {
    /** CSS class excluded from focus-trap exit detection (e.g. trigger button). */
    focusTrapExceptClass?: string;
    /** Called each time the panel becomes visible, before focus trap activation. */
    onActivate?: () => void;
};

/**
 * Sets up close, keyboard, and FocusTrap behavior for a corner dropdown panel.
 * @param display Visibility model ref
 * @param rootEl Template ref to the panel root element
 * @param options Optional focus-trap and activation hooks
 */
export function useDropdownPanel(
    display: Ref<boolean | undefined>,
    rootEl: Ref<HTMLElement | null | undefined>,
    options?: UseDropdownPanelOptions,
) {
    let focusTrap: FocusTrap | null = null;

    const close = () => {
        display.value = false;
    };

    const escapeToClose = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
            close();
        }
    };

    const stopPropagationEvent = (e: Event) => {
        e.stopPropagation();
    };

    const clickOnEnter = (event: KeyboardEvent) => {
        if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            (event.target as HTMLElement).click();
        }
    };

    const activateDropdown = () => {
        options?.onActivate?.();
        focusTrap?.activate();
        autoFocusElement(rootEl.value);
    };

    onMounted(() => {
        if (rootEl.value) {
            focusTrap = new FocusTrap(rootEl.value, close, options?.focusTrapExceptClass);
        }
        if (display.value) {
            activateDropdown();
        }
    });

    onBeforeUnmount(() => {
        focusTrap?.destroy();
    });

    watch(display, (isOpen) => {
        if (isOpen) {
            activateDropdown();
        } else {
            focusTrap?.deactivate();
        }
    });

    return {
        close,
        escapeToClose,
        stopPropagationEvent,
        clickOnEnter,
    };
}
